import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { usePayWithPayPalMutation } from '__generated__/graphql';
import {
  isServer,
  PAYMENT_METHOD_SESSION_STORAGE_ENTRY,
  PaymentMethod,
} from 'utils/constants';
import { isCartInStock } from 'utils/isCartInStock';

import { useCart } from './useCart';
import { useFeature } from './useFeature';

export const usePayPal = (
  pauseCartFetch?: boolean
): [{ fetching: boolean; error: undefined }, () => Promise<void>] => {
  const payPalEnabled = useFeature(PaymentMethod.PAYPAL);
  const { cart, cartResult } = useCart({
    pause: pauseCartFetch || isServer,
  });

  const [payPalResult, payPal] = usePayWithPayPalMutation();
  const router = useRouter();

  const startPayPal = useCallback(async () => {
    if (
      !payPalEnabled ||
      !cart?.id ||
      cartResult.stale ||
      !isCartInStock(cartResult) ||
      !cart?.products.length
    ) {
      return;
    }

    const payPalOperationResult = await payPal({
      input: {
        basketId: cart?.id,
        feHost: window.location.host,
      },
    });

    if (!payPalOperationResult.data?.payPal) {
      return;
    }

    window.sessionStorage.setItem(
      PAYMENT_METHOD_SESSION_STORAGE_ENTRY.PayPal,
      payPalOperationResult.data.payPal.orderNo
    );

    if (payPalOperationResult.data.payPal.url) {
      router.push(payPalOperationResult.data.payPal.url);
    }
  }, [cart, cartResult, payPal, payPalEnabled, router]);

  return [
    {
      fetching: payPalResult.fetching,
      error: undefined,
    },
    startPayPal,
  ];
};
